import { stopMediaStream } from './media-devices';

// Bounded wait: if the recorder never fires `stop`, flush() must not hang the clinician's note
// forever — a stuck note is worse than a lost tail.
const FLUSH_TIMEOUT_MS = 2000;

export class AudioRecorder {
    private readonly audioContext: AudioContext;
    private readonly destinationSource: MediaStreamAudioDestinationNode;
    private readonly mediaRecorder: MediaRecorder;
    private readonly sources: Map<string, MediaStreamAudioSourceNode>;
    private readonly timeSlice: number;
    private readonly abortController: AbortController = new AbortController();
    private isDisposed = false;
    private isStarted = false;
    private disposal?: Promise<void>;
    private flushPromise?: Promise<void>;

    constructor(timeSlice: number) {
        this.audioContext = new AudioContext();
        this.destinationSource = this.audioContext.createMediaStreamDestination();
        this.mediaRecorder = new MediaRecorder(this.destinationSource.stream, {
            mimeType: 'audio/webm;codecs=opus',
        });
        this.sources = new Map();
        this.timeSlice = timeSlice;
    }

    get state() {
        return this.mediaRecorder.state;
    }

    async connectStream(origin: string, stream: MediaStream) {
        if (this.isDisposed) {
            // Called from a React effect reacting to a mic swap/reconnect — losing that race
            // with teardown is a normal timing outcome, not misuse, so this is a silent no-op.
            console.debug('AudioRecorder: ignoring connectStream on a disposed recorder');
            return;
        }

        const previousSource = this.sources.get(origin);
        if (previousSource?.mediaStream === stream) {
            return;
        }

        console.debug(
            `AudioRecorder: audioContext state before connecting stream: ${this.audioContext.state}`,
        );

        const source = this.audioContext.createMediaStreamSource(stream);
        source.connect(this.destinationSource);

        if (previousSource) {
            previousSource.disconnect();
        }

        this.sources.set(origin, source);

        if (this.mediaRecorder.state === 'inactive') {
            if (this.isStarted) {
                console.debug('AudioRecorder: starting recorder after connecting first stream');
                await this.start();
            }
        } else if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    disconnectStream(id: string) {
        if (this.isDisposed) {
            console.debug('AudioRecorder: ignoring disconnectStream on a disposed recorder');
            return;
        }

        const source = this.sources.get(id);

        if (source) {
            source.disconnect();
        }
    }

    async start() {
        if (this.isDisposed) {
            throw new Error('AudioRecorder: cannot start disposed recorder');
        }

        this.isStarted = true;

        if (this.sources.size === 0) {
            console.debug('AudioRecorder: no connected sources, postpone recording');
            return;
        }

        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        if (this.mediaRecorder.state === 'paused') {
            this.mediaRecorder.resume();
        } else {
            this.mediaRecorder.start(this.timeSlice);
        }
    }

    pause() {
        if (this.isDisposed) {
            console.debug('AudioRecorder: ignoring pause on a disposed recorder');
            return;
        }

        this.mediaRecorder.pause();
    }

    // Must never reject — a rejection here would surface as an unresolved navigation blocker and a
    // spurious "generation failed" toast. Bounded by FLUSH_TIMEOUT_MS and memoized.
    flush(): Promise<void> {
        if (!this.flushPromise) {
            this.flushPromise = this.stopAndFlush().catch((error) => {
                console.error('AudioRecorder: flush failed', error);
            });
        }

        return this.flushPromise;
    }

    // Same never-reject rule as flush() — this is teardown-only, and memoized.
    dispose(): Promise<void> {
        if (!this.disposal) {
            this.disposal = this.disposeOnce().catch((error) => {
                console.error('AudioRecorder: dispose failed', error);
            });
        }

        return this.disposal;
    }

    private async disposeOnce(): Promise<void> {
        // Flip this before anything awaits, not just at the end: a call landing mid-teardown could
        // otherwise insert a source after `sources` is cleared below.
        this.isDisposed = true;

        // Join an in-flight flush before aborting the lifetime listener: aborting first would tear
        // down the dataavailable listener flush()'s stop() is about to feed, so flush() would
        // resolve believing the tail was delivered when it never reached the callback. Safe to
        // await unconditionally here — flushPromise never rejects and is bounded by
        // FLUSH_TIMEOUT_MS, and it is only set once a flush has actually started.
        if (this.flushPromise) {
            await this.flushPromise;
        }

        this.abortController.abort();

        if (this.canStop()) {
            this.mediaRecorder.stop();
        }

        this.sources.forEach((source) => source.disconnect());
        this.sources.clear();

        stopMediaStream(this.destinationSource.stream);

        await this.audioContext.close();
    }

    private canStop(): boolean {
        return this.isStarted && this.mediaRecorder.state !== 'inactive';
    }

    private stopAndFlush(): Promise<void> {
        if (!this.canStop()) {
            return Promise.resolve();
        }

        // Local to this flush, not this.abortController: that guards the dataavailable listener for
        // the recorder's lifetime, and aborting it would drop the flush blob it delivers.
        const flushAbort = new AbortController();
        let timeoutId: ReturnType<typeof setTimeout>;

        return new Promise<void>((resolve, reject) => {
            timeoutId = setTimeout(() => {
                console.error(
                    `AudioRecorder: flush timed out after ${FLUSH_TIMEOUT_MS}ms, tail audio may be lost`,
                );
                resolve();
            }, FLUSH_TIMEOUT_MS);

            this.mediaRecorder.addEventListener('stop', () => resolve(), {
                once: true,
                signal: flushAbort.signal,
            });

            try {
                this.mediaRecorder.stop();
                this.isStarted = false;
            } catch (error) {
                reject(error as Error);
            }
        }).finally(() => {
            clearTimeout(timeoutId);
            flushAbort.abort();
        });
    }

    onDataAvailable(callback: (data: Blob) => void) {
        if (this.isDisposed) {
            throw new Error('AudioRecorder: cannot set dataavailable handler on disposed recorder');
        }

        this.mediaRecorder.addEventListener('dataavailable', (event) => callback(event.data), {
            signal: this.abortController.signal,
        });
    }
}
